import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Info, X } from "lucide-react";

const ACCENT_MAP = {
  emerald: { ring: "border-emerald-500/40", text: "text-emerald-300", dot: "bg-emerald-400", soft: "bg-emerald-500/[0.04]" },
  amber: { ring: "border-amber-500/40", text: "text-amber-300", dot: "bg-amber-400", soft: "bg-amber-500/[0.04]" },
  purple: { ring: "border-purple-500/40", text: "text-purple-300", dot: "bg-purple-400", soft: "bg-purple-500/[0.04]" },
  sky: { ring: "border-sky-500/40", text: "text-sky-300", dot: "bg-sky-400", soft: "bg-sky-500/[0.04]" },
  pink: { ring: "border-pink-500/40", text: "text-pink-300", dot: "bg-pink-400", soft: "bg-pink-500/[0.04]" },
  rose: { ring: "border-rose-500/40", text: "text-rose-300", dot: "bg-rose-400", soft: "bg-rose-500/[0.04]" },
  cyan: { ring: "border-cyan-500/40", text: "text-cyan-300", dot: "bg-cyan-400", soft: "bg-cyan-500/[0.04]" },
  lime: { ring: "border-lime-500/40", text: "text-lime-300", dot: "bg-lime-400", soft: "bg-lime-500/[0.04]" },
  teal: { ring: "border-teal-500/40", text: "text-teal-300", dot: "bg-teal-400", soft: "bg-teal-500/[0.04]" },
  orange: { ring: "border-orange-500/40", text: "text-orange-300", dot: "bg-orange-400", soft: "bg-orange-500/[0.04]" },
};

const NUMS = ["①", "②", "③", "④", "⑤", "⑥"];

/**
 * Compact colorful "how it works" panel. Pinned to the top of each page.
 * - Persists collapse state per `id` in localStorage so power users don't see it every visit.
 * - Each step: { label, body, accent }.
 */
export default function HowItWorks({ id, tag, headline, steps }) {
  const storageKey = `mindstein.hiw.${id}.collapsed`;
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === "1") setCollapsed(true);
    } catch { /* ignore */ }
  }, [storageKey]);

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(storageKey, next ? "1" : "0");
      } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div
      data-testid={`how-it-works-${id}`}
      className="border border-[#27272A] bg-gradient-to-br from-[#0E0E0E] to-[#0A0A0A]"
    >
      <button
        type="button"
        onClick={toggle}
        data-testid={`how-it-works-toggle-${id}`}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#141414] group"
      >
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300">
          <Info size={11} /> HOW_IT_WORKS
        </span>
        {tag && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            · {tag}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500 group-hover:text-white">
          {collapsed ? (
            <>
              SHOW <ChevronRight size={10} />
            </>
          ) : (
            <>
              HIDE <ChevronDown size={10} />
            </>
          )}
        </span>
      </button>
      {!collapsed && (
        <div className="px-3 pb-3">
          {headline && (
            <p className="text-[12px] text-zinc-300 leading-relaxed mb-3">
              {headline}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
            {steps.map((s, i) => {
              const a = ACCENT_MAP[s.accent] || ACCENT_MAP.amber;
              return (
                <div
                  key={i}
                  data-testid={`how-it-works-${id}-step-${i + 1}`}
                  className={`relative border ${a.ring} ${a.soft} p-3`}
                >
                  <div className={`absolute left-0 top-0 h-full w-[3px] ${a.dot}`} />
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-lg ${a.text}`}>
                      {NUMS[i] || `${i + 1}.`}
                    </span>
                    <span className={`text-[10px] uppercase tracking-[0.2em] font-mono ${a.text}`}>
                      STEP {i + 1}
                    </span>
                  </div>
                  <div className="mt-1.5 font-mono text-[12.5px] text-white tracking-tight leading-tight">
                    {s.label}
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-400 leading-snug">
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Tiny inline X-dismiss variant for embedded sub-sections — exported for reuse. */
export function HowItWorksMini({ id, label, steps }) {
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  return (
    <div
      data-testid={`how-it-works-mini-${id}`}
      className="border border-amber-500/30 bg-amber-500/[0.04] px-3 py-2 text-[11px] text-zinc-300 flex items-start gap-2"
    >
      <Info size={12} className="text-amber-400 mt-0.5 shrink-0" />
      <div className="flex-1">
        <span className="text-amber-300 font-mono uppercase tracking-wider text-[10px] mr-2">
          {label}
        </span>
        {steps.map((s, i) => (
          <span key={i}>
            <span className="text-white font-mono">{i + 1}.</span> {s}{" "}
          </span>
        ))}
      </div>
      <button
        onClick={() => setClosed(true)}
        className="text-zinc-500 hover:text-white"
        aria-label="dismiss"
      >
        <X size={11} />
      </button>
    </div>
  );
}
